<template>
  <section class="relative transition-colors duration-300">
    
    <MotifBackdrop
      :show-grid="false"
      :show-tiled-pattern="false"
      :show-paper-field="false"
      :random-icons="true"
      :random-density="1"
      :size-min="28"
      :size-max="60"
      :opacity-min="0.10"
      :opacity-max="0.28"
      :kinds="['logo', 'code', 'terminal', 'braces', 'db', 'link']"
      :weights="{ logo: 2, code: 1.5, terminal: 1, mail: 0 }"
      :seed="2025"
    />

    <div class="text-center">
      <div class="max-w-2xl mx-auto text-center relative mb-2">
        <h2
          class="relative z-10 text-4xl sm:text-5xl font-extrabold tracking-tight 
                 bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 bg-clip-text text-transparent
                 dark:text-ink-50 dark:bg-none"
        >
          {{ serviceTitle?.titre || (effectiveLang === 'en' ? 'What We Do Best' : 'Ce que nous faisons de mieux') }}
        </h2>
        
        <p v-if="serviceTitle?.description" 
           class="mt-4 max-w-3xl mx-auto text-base sm:text-lg 
                  text-slate-600 dark:text-ink-300">
          {{ serviceTitle.description }}
        </p>
      </div>

      <div class="mx-auto mt-6 h-px w-40 bg-gradient-to-r from-transparent via-brand-300 dark:via-brand-800 to-transparent"></div>

      <div class="flex justify-center gap-3 sm:gap-4 mt-6" v-if="isEditMode">
        <button
          @click="editServices"
          class="flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-medium rounded-full shadow-sm transition
                 text-slate-800 bg-white border border-slate-200 hover:bg-slate-50
                 dark:text-ink-100 dark:bg-ink-800 dark:border-ink-700 dark:hover:bg-ink-700 relative z-20"
        >
          <i class="fas fa-pen-to-square text-sm text-brand-600 dark:text-brand-400"></i>
          {{ effectiveLang === 'en' ? 'Edit' : 'Éditer' }}
        </button>
      </div>
    </div>

    <div class="mt-10 sm:mt-12 grid gap-6 sm:gap-7 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      
      <template v-if="loading">
        <div v-for="n in 8" :key="'skel-' + n" class="rounded-3xl overflow-hidden shadow-sm bg-white ring-1 ring-slate-200 dark:bg-ink-900 dark:ring-ink-800">
          <div class="aspect-[16/10] bg-slate-100 dark:bg-ink-800 animate-pulse"></div>
          <div class="p-5">
            <div class="h-4 w-2/3 bg-slate-100 dark:bg-ink-800 rounded mb-3 animate-pulse"></div>
            <div class="h-3 w-full bg-slate-100 dark:bg-ink-800 rounded mb-2 animate-pulse"></div>
            <div class="h-3 w-5/6 bg-slate-100 dark:bg-ink-800 rounded animate-pulse"></div>
          </div>
        </div>
      </template>

      <div
        v-else-if="displayServices.length === 0"
        class="col-span-full text-center px-6 py-12 rounded-2xl border shadow-sm
               bg-white border-slate-200
               dark:bg-ink-900 dark:border-ink-800"
      >
        <div class="inline-grid h-12 w-12 place-items-center rounded-xl mb-3 bg-slate-100 text-slate-500 dark:bg-ink-800 dark:text-ink-400">
          <i class="fa-regular fa-star"></i>
        </div>
        <h3 class="text-lg font-semibold text-slate-900 dark:text-ink-100">
          {{ popularOnly ? (effectiveLang === 'en' ? 'No popular services yet' : 'Aucun service populaire pour le moment') : (effectiveLang === 'en' ? 'No services yet' : 'Aucun service pour le moment') }}
        </h3>
        <p class="mt-1 text-sm text-slate-600 dark:text-ink-400" v-if="popularOnly">
          {{ effectiveLang === 'en' ? 'Mark services as “Popular” from the admin to feature them here.' : 'Marquez des services comme “Populaires” dans l’admin pour les afficher ici.' }}
        </p>
      </div>

      <article
        v-else
        v-for="(srv, index) in displayServices"
        :key="srv?.id || srv?.slug || srv?.titre || srv?.image_url || index"
        class="group relative flex flex-col h-full min-h-[360px] rounded-3xl overflow-hidden transition-all duration-500
               border border-transparent
               bg-white/95 backdrop-blur-md shadow-sm ring-1 ring-ink-900/5
               dark:bg-ink-800/60 dark:backdrop-blur-md dark:shadow-none dark:ring-white/10
               hover:-translate-y-1 hover:shadow-2xl hover:shadow-brand-500/10
               hover:border-brand-500/50 dark:hover:border-brand-400/50"
      >
        <router-link :to="makeServiceLink(srv)" class="absolute inset-0 z-0 focus:outline-none" aria-label="Voir le détail">
          <span class="sr-only">{{ srv.titre }}</span>
        </router-link>

        <div class="relative aspect-[16/9] overflow-hidden pointer-events-none"> <img
            :src="srv.image_url || placeholder"
            :alt="imageAlt(srv)"
            class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          
          <div class="absolute inset-0 bg-ink-900/20 dark:bg-ink-950/40 transition-colors group-hover:bg-ink-900/0"></div>

          <div class="absolute top-3 right-3 flex flex-col gap-2 items-end z-10">
             <div v-if="srv.badge" class="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider text-white bg-ink-900/80 backdrop-blur-sm shadow-sm">
              {{ srv.badge }}
            </div>
            
            <div v-if="Number(srv.is_popular) === 1" class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-amber-300 bg-black/60 backdrop-blur-md border border-white/10 shadow-sm">
              <i class="fa-solid fa-star text-[9px]"></i>
              <span>TOP</span>
            </div>
          </div>

          <div class="absolute bottom-3 left-3 z-10">
            <div class="h-12 w-12 grid place-items-center rounded-xl 
                        bg-white/95 dark:bg-ink-800/90 backdrop-blur-md 
                        shadow-lg ring-1 ring-black/5 dark:ring-white/10
                        text-brand-600 dark:text-brand-400">
              <i :class="iconClassFromService(srv)" class="text-xl"></i>
            </div>
          </div>
        </div>

        <div class="relative p-6 flex-1 flex flex-col pointer-events-none"> <h3 class="text-xl font-bold tracking-tight mb-2
                     text-ink-900 dark:text-ink-50 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors relative z-10">
            {{ srv.titre || (effectiveLang === 'en' ? 'Service' : 'Service') }}
          </h3>

          <p class="text-sm leading-relaxed text-ink-600 dark:text-ink-300 line-clamp-3 mb-6 relative z-10">
            {{ srv.description || (effectiveLang === 'en' ? 'Description' : 'Description du service') }}
          </p>

          <div class="mt-auto pt-4 border-t border-ink-100 dark:border-ink-700/50 flex items-center justify-between relative z-10">
            <span class="text-sm font-semibold text-ink-900 dark:text-ink-100 flex items-center gap-2 group/btn">
              <span class="relative">
                {{ effectiveLang === 'en' ? 'Learn more' : 'En savoir plus' }}
                <span class="absolute left-0 -bottom-0.5 w-0 h-px bg-brand-600 dark:bg-brand-400 transition-all duration-300 group-hover:w-full"></span>
              </span>
              <i class="fa-solid fa-arrow-right text-xs text-brand-600 dark:text-brand-400 transition-transform duration-300 group-hover:translate-x-1"></i>
            </span>
          </div>
        </div>
      </article>
    </div>

    <div class="flex justify-center mt-12 sm:mt-14 flex-wrap gap-3 sm:gap-4">
      <router-link
        v-for="(button, idx) in serviceButtons"
        :key="'cta-' + idx"
        :to="button.action || '#'"
        class="relative group inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 text-sm font-semibold
               rounded-full overflow-hidden transition-all duration-300
               shadow-[0_2px_10px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_18px_rgba(0,0,0,0.08)]
               focus:outline-none focus:ring-2 focus:ring-offset-2 z-10"
        :class="[
          (button.style === 'primary')
            ? 'text-white bg-brand-600 focus:ring-brand-300 hover:bg-brand-700 dark:bg-brand-700 dark:hover:bg-brand-600 dark:focus:ring-brand-900'
            : 'text-slate-800 bg-white border border-slate-200 focus:ring-slate-300 hover:bg-slate-50 dark:text-ink-200 dark:bg-ink-800 dark:border-ink-700 dark:hover:bg-ink-700'
        ]"
      >
        <span class="relative z-10">{{ button.label || (effectiveLang === 'en' ? 'Discover' : 'Découvrir') }}</span>
        <i class="fa-solid fa-arrow-right text-xs relative z-10 transition-transform duration-300 group-hover:translate-x-1"></i>
        <span class="absolute inset-0 rounded-full bg-black scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] transform-gpu pointer-events-none opacity-10 dark:opacity-30"></span>
      </router-link>
    </div>
  </section>
</template>

<script>
// Le script n'a PAS besoin de changement pour le dark mode, 
// car tout est géré via les classes CSS dans le template.
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";
import { mapGetters } from "vuex";
import MotifBackdrop from "@/components/ui/MotifBackdrop.vue";

export default {
  name: "ServicesComponent",
  components: { MotifBackdrop },
  props: {
    isEditMode: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    pageCode: { type: String, default: "home" },
    langue: { type: String, default: null },
    popularOnly: { type: Boolean, default: false },
    limit: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: true,
      serviceTitle: null,
      services: [],
      serviceButtons: [],
      _resolvedPageId: null,
      placeholder: "https://placehold.co/1200x675?text=Service",
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),
    effectiveLang() {
      return this.langue || this.getLangue || "fr";
    },
    effectivePageCode() {
      if (this.pageCode) return this.pageCode;
      const n = this.$route?.name || "";
      if (/about/i.test(n)) return "about";
      if (/portfolio/i.test(n)) return "portfolio";
      if (/contact/i.test(n)) return "contact";
      if (/footer/i.test(n)) return "footer";
      return "home";
    },
    primaryCta() {
      const first = (this.serviceButtons || []).find(
        (b) => (b.style || "").toLowerCase() === "primary"
      );
      return first?.action || (this.effectiveLang === "en" ? "/en/contact" : "/fr/contact");
    },
    displayServices() {
      let arr = Array.isArray(this.services) ? [...this.services] : [];
      if (this.popularOnly) {
        arr = arr.filter(s => Number(s?.is_popular) === 1);
        arr.sort((a, b) => {
          const ra = Number(a?.popular_rank ?? 9999);
          const rb = Number(b?.popular_rank ?? 9999);
          return ra - rb || (Number(b?.id || 0) - Number(a?.id || 0));
        });
      }
      if (this.limit && Number(this.limit) > 0) {
        arr = arr.slice(0, Number(this.limit));
      }
      return arr;
    },
  },
  watch: {
    pageId(newVal) { if (newVal) { this._resolvedPageId = Number(newVal); this.fetchByPageId(this._resolvedPageId); } },
    effectiveLang() { if (!this.pageId) this.autoResolveAndFetch(); },
    "$route.fullPath"() { if (!this.pageId) this.autoResolveAndFetch(); },
    effectivePageCode() { if (!this.pageId) this.autoResolveAndFetch(); },
    isEditMode() { if (Number.isFinite(this._resolvedPageId)) { this.fetchByPageId(this._resolvedPageId); } },
    popularOnly() { if (Number.isFinite(this._resolvedPageId)) this.fetchByPageId(this._resolvedPageId); },
    limit() { if (Number.isFinite(this._resolvedPageId)) this.fetchByPageId(this._resolvedPageId); },
  },
  methods: {
    isIconToken(v) { return typeof v === "string" && v.trim().toLowerCase().startsWith("icon:"); },
    iconClassFromToken(token) {
      const fallback = "fa-solid fa-briefcase";
      if (!this.isIconToken(token)) return fallback;
      const raw = token.slice(5).trim();
      const parts = raw.split(/\s+/).filter(Boolean);
      if (parts.length >= 2 && parts[0].startsWith("fa-") && parts[1].startsWith("fa-")) return `${parts[0]} ${parts[1]}`;
      if (parts.length === 1 && parts[0].startsWith("fa-")) return `fa-solid ${parts[0]}`;
      return fallback;
    },
    iconClassFromService(srv = {}) { const tok = srv.icon_alt || (this.isIconToken(srv.alt) ? srv.alt : null); return this.iconClassFromToken(tok); },
    iconTitle(srv = {}) { return srv.titre || "Service"; },
    imageAlt(srv = {}) { if (this.isIconToken(srv.alt)) return srv.titre || "Service"; return srv.alt || srv.titre || "Service"; },
    async autoResolveAndFetch() {
      try {
        if (this.pageId) { this._resolvedPageId = Number(this.pageId); } else {
          const lang = this.effectiveLang;
          const code = this.effectivePageCode;
          const idOrObj = await fetchPageIdByCode(code, lang);
          const pid = typeof idOrObj === "number" ? idOrObj : idOrObj?.page_id ?? idOrObj?.id ?? null;
          this._resolvedPageId = Number(pid);
        }
        if (Number.isFinite(this._resolvedPageId)) { await this.fetchByPageId(this._resolvedPageId); } else { this.loading = false; }
      } catch (e) { this.loading = false; }
    },
    async fetchByPageId(pid) {
      this.loading = true;
      try {
        const params = {
          page_id: pid,
          ...(this.isEditMode ? { includeDraft: 1 } : {}),
          ...(this.popularOnly ? { popularOnly: 1 } : {}),
          ...(this.limit ? { limit: Number(this.limit) } : {}),
        };
        const { data } = await http.get("services", { params });
        this.serviceTitle = data?.serviceTitle || {};
        const raw = Array.isArray(data?.services) ? data.services : [];
        this.services = raw.map((s) => ({
          ...s,
          badge: this.isEditMode && s.status === "draft" ? (this.effectiveLang === "en" ? "Draft" : "Brouillon") : s.badge,
        }));
        this.serviceButtons = Array.isArray(data?.boutons) ? data.boutons : [];
      } catch (error) { this.serviceTitle = {}; this.services = []; this.serviceButtons = []; } finally { this.loading = false; }
    },
    isExternal(href) { return /^https?:\/\//i.test(String(href || "")); },
    ensureLocalePrefix(path) {
      if (!path) return this.primaryCta || "#";
      if (this.isExternal(path)) return path;
      const lang = this.effectiveLang === "en" ? "en" : "fr";
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },
    makeServiceLink(srv) {
      if (srv?.slug) { return { name: 'service-detail', params: { langue: this.effectiveLang, slug: srv.slug } }; }
      if (srv?.action) return this.ensureLocalePrefix(srv.action);
      return this.primaryCta || '#';
    },
    editServices() { this.$emit("edit", { serviceTitle: this.serviceTitle, services: this.services }); },
    addServices() { this.$emit("add"); },
  },
  async mounted() {
    if (this.pageId) {
      this._resolvedPageId = Number(this.pageId);
      await this.fetchByPageId(this._resolvedPageId);
    } else {
      await this.autoResolveAndFetch();
    }
  },
};
</script>

<style scoped>
/* Ce bloc gère l'effet de flou sous le titre. 
   Il faut le modifier pour qu'il soit sombre en mode dark. */
.title-fade-band::before {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 50%;
  translate: 0 -50%;
  width: min(95%, 48rem);
  height: 12rem;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, .9),
    rgba(255, 255, 255, .75) 90%,
    rgba(255, 255, 255, 0)
  );
  filter: blur(10px);
  border-radius: 20px;
  z-index: 0;
}

/* Version DARK du flou */
:global(.dark) .title-fade-band::before {
  background: linear-gradient(
    180deg,
    rgba(9, 9, 11, .9),   /* Zinc-950 approx */
    rgba(9, 9, 11, .75) 90%,
    rgba(9, 9, 11, 0)
  );
}

@media (min-width:640px) {
  .title-fade-band::before {
    height: 6rem;
    filter: blur(6px);
  }
}
</style>